import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import styles from './index.module.scss';
import BlockA from '../../_block_components/block_a';
import BlockB from '../../_block_components/block_b';
import UnknownBlock from '../../_block_components/unknown_block';
import {
  selectCartItems,
  selectPromoItems,
  selectCartTotal,
  selectCartItemsCount,
  removeFromCart,
  removePromoItemFromCart,
  updateCartItemQuantity,
  updatePromoItemQuantity,
  clearCart,
  saveOrderData,
} from '../../../../redux/slices/cartSlice';
import { submitOrder } from '../../../../axios/submit_order_axios';

const CartV1 = ({ publication, globalIndex }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector(selectCartItems);
  const promoItems = useSelector(selectPromoItems);
  const cartTotal = useSelector(selectCartTotal);
  const cartItemsCount = useSelector(selectCartItemsCount);
  const [publicationData, setPublicationData] = React.useState(null);
  const [contentData, setContentData] = React.useState(null);
  const [path_to_folder, setPath_to_folder] = React.useState(null);
  const [deliveryMethod, setDeliveryMethod] = React.useState('pickup');
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = React.useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = React.useState(false);
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = React.useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState(null);
  const [orderForm, setOrderForm] = React.useState({
    name: '',
    phone: '',
    email: '',
    comment: '',
  });
  const [fieldErrors, setFieldErrors] = React.useState({
    name: false,
    phone: false,
  });
  const [agreementLinks, setAgreementLinks] = React.useState([]);

  React.useEffect(() => {
    if (publication) {
      setPublicationData(publication);
    }
  }, [publication]);

  React.useEffect(() => {
    if (publicationData) {
      // Парсим content
      if (publicationData.content) {
        try {
          const parsedContent = JSON.parse(publicationData.content);
          setContentData(parsedContent);
        } catch (error) {
          setContentData(null);
        }
      }

      // Создаем path_to_folder
      if (publicationData.path && publicationData.eng_name) {
        const pathValue = `https://${publicationData.path}${publicationData.eng_name}/`;
        setPath_to_folder(pathValue);
      }
    }
  }, [publicationData]);

  // Извлекаем заголовок из блока с тегом header_block
  const title = React.useMemo(() => {
    if (!contentData || !Array.isArray(contentData)) return null;

    const headerBlock = contentData.find((block) => (block.boock_tag === 'header_block' || block.block_tag === 'header_block'));
    if (!headerBlock || !headerBlock.block_content || !Array.isArray(headerBlock.block_content)) {
      return null;
    }

    // Ищем текстовый контент в блоке
    const textContent = headerBlock.block_content.find(
      (content) => (content.cotent_type === 'text' || content.content_type === 'text') || (content.cotent_type === 'long_text' || content.content_type === 'long_text'),
    );

    return textContent?.content_text || null;
  }, [contentData]);

  // Извлекаем ссылки согласия из contentData и сохраняем в state
  React.useEffect(() => {
    // console.log('=== DEBUG: agreementLinks extraction ===');
    // console.log('contentData:', contentData);
    
    if (!contentData || !Array.isArray(contentData)) {
      // console.log('contentData is null or not array, setting default links');
      setAgreementLinks(['Политикой конфиденциальности*privacy_policy', 'Договора-оферты*terms_of_service']);
      return;
    }

    // console.log('contentData length:', contentData.length);
    
    // Собираем все элементы с content_text, содержащим звездочку
    const textsWithStars = [];
    
    for (let blockIndex = 0; blockIndex < contentData.length; blockIndex++) {
      const block = contentData[blockIndex];
      // console.log(`Checking block[${blockIndex}]:`, block);
      
      if (block.block_content && Array.isArray(block.block_content)) {
        // console.log(`block.block_content length:`, block.block_content.length);
        
        for (let contentIndex = 0; contentIndex < block.block_content.length; contentIndex++) {
          const content = block.block_content[contentIndex];
          // console.log(`Checking content[${contentIndex}]:`, content);
          // console.log(`content.content_text includes "*":`, content.content_text?.includes('*'));
          
          // Ищем по content_text, который содержит звездочку
          if (
            ((content.cotent_type === 'text' || content.content_type === 'text') || (content.cotent_type === 'long_text' || content.content_type === 'long_text')) &&
            content.content_text &&
            content.content_text.includes('*')
          ) {
            // console.log('Found agreement text with *:', content.content_text);
            textsWithStars.push(content.content_text);
          }
        }
      }
    }

    // console.log('All texts with stars found:', textsWithStars);
    // console.log('Count of texts with stars:', textsWithStars.length);

    // Сохраняем найденные ссылки в state
    if (textsWithStars.length > 0) {
      setAgreementLinks(textsWithStars);
    } else {
      // Если не нашли, используем дефолтные
      setAgreementLinks(['Политикой конфиденциальности*privacy_policy', 'Договора-оферты*terms_of_service']);
    }
  }, [contentData]);

  // Функция для парсинга текста со ссылками через звездочку (как в футере)
  const parseTextWithLinks = (text) => {
    if (!text || typeof text !== 'string') {
      return text;
    }

    // Проверяем, есть ли звездочки в тексте
    if (!text.includes('*')) {
      return text;
    }

    // Выводим в консоль только когда есть звездочка
    // console.log('=== DEBUG: parseTextWithLinks (found *) ===');
    // console.log('Input text:', text);
    
    const parts = [];
    let lastIndex = 0;
    // Регулярное выражение: текст до звездочки, звездочка, имя страницы (до пробела или конца строки)
    const regex = /([^*]+)\*([^\s*]+)/g;
    let match;
    let keyCounter = 0;
    let hasMatches = false;

    while ((match = regex.exec(text)) !== null) {
      hasMatches = true;
      // console.log('Match found:', {
      //   fullMatch: match[0],
      //   linkText: match[1],
      //   sectionName: match[2],
      //   index: match.index
      // });
      
      // Добавляем текст до ссылки
      if (match.index > lastIndex) {
        const textBefore = text.substring(lastIndex, match.index);
        if (textBefore) {
          // console.log('Adding text before link:', textBefore);
          parts.push(textBefore);
        }
      }
      
      // Текст слева от звездочки - отображаемое имя ссылки
      const linkText = match[1].trim();
      // Текст справа от звездочки - имя страницы
      const sectionName = match[2].trim();
      
      // Формируем путь (как в футере)
      const path = sectionName === 'main_tab' ? '/' : `/${sectionName}`;
      // console.log('Creating link:', { linkText, sectionName, path });
      
      parts.push(
        <Link
          key={`link-${keyCounter++}`}
          to={path}
          className={styles.paymentInfoLink}
        >
          {linkText}
        </Link>
      );
      
      // Обновляем lastIndex на позицию после полного совпадения
      lastIndex = match.index + match[0].length;
    }

    // Добавляем оставшийся текст после последней ссылки
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        // console.log('Adding remaining text after last link:', remainingText);
        parts.push(remainingText);
      }
    }

    // console.log('Final parts count:', parts.length);
    // console.log('Final parts:', parts);
    // console.log('hasMatches:', hasMatches);
    
    // Если были совпадения, возвращаем массив частей, иначе возвращаем исходный текст
    const result = hasMatches && parts.length > 0 ? <>{parts}</> : text;
    // console.log('Final result type:', typeof result);
    // console.log('Final result:', result);
    return result;
  };

  // Предфильтрация: убираем пустые и header блоки
  const filteredBlocks = React.useMemo(() => {
    if (!contentData || !Array.isArray(contentData)) {
      return [];
    }

    return contentData.filter((blockData) => {
      // Пропускаем header_block (используется для заголовка)
      if (blockData && (blockData.boock_tag === 'header_block' || blockData.block_tag === 'header_block')) {
        return false;
      }

      // Пропускаем блоки без данных
      if (!blockData || !(blockData.boock_tag || blockData.block_tag)) {
        return false;
      }

      // Пропускаем блоки с пустым block_content
      if (
        !blockData.block_content ||
        !Array.isArray(blockData.block_content) ||
        blockData.block_content.length === 0
      ) {
        return false;
      }

      return true;
    });
  }, [contentData]);

  const formatPrice = (price) => {
    return price.toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleRemoveItem = (id) => {
    if (window.confirm('Удалить товар из корзины?')) {
      dispatch(removeFromCart({ id }));
    }
  };

  const handleRemovePromoItem = (id) => {
    if (window.confirm('Удалить товар из корзины?')) {
      dispatch(removePromoItemFromCart({ id }));
    }
  };

  const handleQuantityChange = (id, newQuantity) => {
    if (newQuantity > 0) {
      dispatch(updateCartItemQuantity({ id, quantity: newQuantity }));
    }
  };

  const handlePromoQuantityChange = (id, newQuantity) => {
    if (newQuantity > 0) {
      dispatch(updatePromoItemQuantity({ id, quantity: newQuantity }));
    }
  };


  const handleOrderFormChange = (field, value) => {
    setOrderForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNextToPayment = () => {
    // Валидация
    const errors = {
      name: !orderForm.name.trim(),
      phone: !orderForm.phone.trim(),
    };

    setFieldErrors(errors);

    if (errors.name || errors.phone) {
      // Сбрасываем ошибки через 1.5 секунды
      setTimeout(() => {
        setFieldErrors({ name: false, phone: false });
      }, 1500);
      return;
    }

    // Закрываем окно оформления заказа и открываем окно выбора способа оплаты
    setIsCheckoutModalOpen(false);
    setIsPaymentMethodModalOpen(true);
  };

  const handleSubmitOrder = async () => {
    // Формируем данные заказа
    const orderData = {
      items: cartItems.map((item) => ({
        itemId: item.itemId,
        itemName: item.itemName,
        itemImage: item.itemImage,
        itemPath: item.itemPath,
        variant: item.variant,
        variantIndex: item.variantIndex,
        price: item.price,
        quantity: item.quantity,
      })),
      promoItems: promoItems.map((item) => ({
        itemId: item.itemId,
        itemName: item.itemName,
        itemImage: item.itemImage,
        itemPath: item.itemPath,
        variant: item.variant,
        variantIndex: item.variantIndex,
        originalPrice: item.originalPrice,
        discountedPrice: item.discountedPrice,
        quantity: item.quantity,
        promoCode: item.promoCode,
        discountPercent: item.discountPercent,
      })),
      total: cartTotal,
      deliveryMethod,
      customerInfo: orderForm,
      paymentMethod: selectedPaymentMethod,
      orderDate: new Date().toISOString(),
    };

    // Выводим данные заказа в дебаг перед отправкой
    // console.log('=== ORDER DATA BEFORE SUBMISSION ===');
    // console.log('Order Data:', orderData);
    // console.log('=== END ORDER DATA ===');

    // Сохраняем данные заказа в Redux перед отправкой
    dispatch(saveOrderData(orderData));

    try {
      // Отправляем заказ на сервер и ждем ответ
      const response = await submitOrder(orderData);

      // Проверяем статус ответа (submitOrder возвращает response.data)
      if (response && response.status === 'ok') {
        // Очищаем корзину и форму
        dispatch(clearCart());
        setOrderForm({
          name: '',
          phone: '',
          email: '',
          comment: '',
        });
        setSelectedPaymentMethod(null);
        setIsPaymentMethodModalOpen(false);
        
        // Перенаправляем на страницу успешного заказа
        navigate('/order_accepted');
      } else {
        // console.error('Ошибка при отправке заказа: статус не ok');
      }
    } catch (error) {
      // console.error('Ошибка при отправке заказа:', error);
    }
  };

  const renderBlock = (blockData, index) => {
    if (!blockData || !(blockData.boock_tag || blockData.block_tag)) {
      return <UnknownBlock key={index} blockData={blockData} path_to_folder={path_to_folder} />;
    }

    switch (blockData.boock_tag || blockData.block_tag) {
      case 'block_a':
        return (
          <BlockA
            key={index}
            blockData={blockData}
            path_to_folder={path_to_folder}
            blockIndex={index}
          />
        );
      case 'block_b':
        return (
          <BlockB
            key={index}
            blockData={blockData}
            path_to_folder={path_to_folder}
            blockIndex={index}
          />
        );
      default:
        return (
          <UnknownBlock
            key={index}
            blockData={blockData}
            path_to_folder={path_to_folder}
            blockIndex={index}
          />
        );
    }
  };

  return (
    <div className={styles.cartV1}>
      <div className={styles.container}>
        {title && (
          <div className={styles.header}>
            <h1 className={styles.title}>{title}</h1>
          </div>
        )}

        <div className={styles.cartContent}>
          <div className={styles.cartItems}>
            <div className={styles.cartHeader}>
              <h2 className={styles.sectionTitle}>
                Товары в корзине {cartItemsCount > 0 && `(${cartItemsCount})`}
              </h2>
            </div>

            {cartItemsCount === 0 ? (
              <div className={styles.emptyCart}>
                <p>Корзина пуста</p>
              </div>
            ) : (
              <div className={styles.itemsList}>
                {/* Обычные товары */}
                {cartItems.map((item) => {
                  // console.log('=== CART ITEM FULL ===', item);
                  // console.log('=== CART ITEM IMAGE URL ===', item.itemImage);
                  // console.log('=== CART ITEM itemPath ===', item.itemPath);
                  return (
                    <div key={item.id} className={styles.cartItem}>
                      {item.itemImage ? (
                        <img 
                          src={item.itemImage} 
                          alt={item.itemName} 
                          className={styles.itemImage}
                          onError={(e) => {
                            // console.error('=== IMAGE LOAD ERROR ===', item.itemImage);
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className={styles.noImage}>Нет фото</div>
                      )}
                      <div className={styles.itemDetails}>
                      <h3 className={styles.itemName}>{item.itemName}</h3>
                      {item.variant && item.variant.toLowerCase().trim() !== 'none' && (
                        <p className={styles.itemVariant}>
                          {item.variantName ? `${item.variantName} - ${item.variant}` : item.variant}
                        </p>
                      )}
                      <p className={styles.itemPrice}>{formatPrice(item.price)} ₽</p>
                    </div>
                    <div className={styles.itemControls}>
                      <div className={styles.quantityControl}>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}>
                          −
                        </button>
                        <span>{item.quantity}</span>
                        <button onClick={() => handleQuantityChange(item.id, item.quantity + 1)}>
                          +
                        </button>
                      </div>
                      <p className={styles.itemTotal}>
                        {formatPrice(item.price * item.quantity)} ₽
                      </p>
                      <button
                        className={styles.removeButton}
                        onClick={() => handleRemoveItem(item.id)}>
                        ×
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Товары с промокодом */}
              {promoItems.length > 0 && (
                <>
                  <div className={styles.promoSectionDivider}>
                    <span>Товары со скидкой</span>
                  </div>
                  {promoItems.map((item) => {
                    // console.log('=== PROMO ITEM ===', item);
                    // console.log('=== PROMO ITEM IMAGE ===', item.itemImage);
                    return (
                        <div key={item.id} className={`${styles.cartItem} ${styles.promoItem}`}>
                          {item.itemImage && (
                            <img
                              src={item.itemImage}
                              alt={item.itemName}
                              className={styles.itemImage}
                            />
                          )}
                          {!item.itemImage && (
                            <div className={styles.noImage}>Нет фото</div>
                          )}
                          <div className={styles.itemDetails}>
                          <h3 className={styles.itemName}>{item.itemName}</h3>
                          {item.variant && item.variant.toLowerCase().trim() !== 'none' && (
                            <p className={styles.itemVariant}>
                              {item.variantName ? `${item.variantName} - ${item.variant}` : item.variant}
                            </p>
                          )}
                          <p className={styles.promoCode}>Промокод: {item.promoCode}</p>
                          <p className={styles.promoDiscount}>Скидка: {item.discountPercent}%</p>
                        </div>
                        <div className={styles.itemControls}>
                          <div className={styles.promoPricesVertical}>
                            <p className={styles.originalPriceStrike}>
                              {formatPrice(item.originalPrice)} ₽
                            </p>
                            <p className={styles.discountedPriceLarge}>
                              {formatPrice(item.discountedPrice)} ₽
                            </p>
                          </div>
                          <button
                            className={styles.removeButton}
                            onClick={() => handleRemovePromoItem(item.id)}>
                            ×
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          <div className={styles.cartSummary}>
            <h2 className={styles.sectionTitle}>Итого</h2>
            <div className={styles.summaryDetails}>
              <div className={styles.summaryRow}>
                <span>Товары:</span>
                <span>{formatPrice(cartTotal)} ₽</span>
              </div>
              <div 
                className={`${styles.summaryRow} ${styles.deliveryRow}`}
                onClick={() => setIsDeliveryModalOpen(true)}
              >
                <span>Доставка:</span>
                <span className={styles.deliveryValue}>
                  {deliveryMethod === 'pickup' ? 'Самовывоз (0 ₽)' : 'Доставка (0 ₽)'}
                  <svg className={styles.editIcon} width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                  </svg>
                </span>
              </div>
              <div className={`${styles.summaryRow} ${styles.total}`}>
                <span>Итого:</span>
                <span>{formatPrice(cartTotal)} ₽</span>
              </div>
            </div>
            <button 
              className={styles.checkoutButton} 
              disabled={cartItemsCount === 0}
              onClick={() => setIsCheckoutModalOpen(true)}
            >
              Оформить заказ
            </button>
          </div>
          
          {/* Модальное окно выбора доставки */}
          {isDeliveryModalOpen && (
            <div className={styles.modalOverlay} onClick={() => setIsDeliveryModalOpen(false)}>
              <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.modalTitle}>Способ получения</h3>
                
                <div className={styles.deliveryOptions}>
                  <label 
                    className={`${styles.deliveryOption} ${deliveryMethod === 'pickup' ? styles.selected : ''}`}
                  >
                    <input
                      type="radio"
                      name="delivery"
                      value="pickup"
                      checked={deliveryMethod === 'pickup'}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                    />
                    <div className={styles.optionContent}>
                      <div className={styles.optionHeader}>
                        <span className={styles.optionTitle}>Самовывоз</span>
                        <span className={styles.optionPrice}>0 ₽</span>
                      </div>
                      <p className={styles.optionDescription}>
                        Забрать заказ самостоятельно
                      </p>
                    </div>
                  </label>
                  
                  <label 
                    className={`${styles.deliveryOption} ${styles.disabled} ${deliveryMethod === 'delivery' ? styles.selected : ''}`}
                  >
                    <input
                      type="radio"
                      name="delivery"
                      value="delivery"
                      checked={deliveryMethod === 'delivery'}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                      disabled
                    />
                    <div className={styles.optionContent}>
                      <div className={styles.optionHeader}>
                        <span className={styles.optionTitle}>Доставка</span>
                        <span className={styles.optionPrice}>0 ₽</span>
                      </div>
                      <p className={styles.optionDescription}>
                        <span className={styles.unavailable}>Доставка временно недоступна</span>
                      </p>
                    </div>
                  </label>
                </div>
                
                <div className={styles.modalActions}>
                  <button 
                    className={styles.confirmButton}
                    onClick={() => setIsDeliveryModalOpen(false)}
                  >
                    Подтвердить
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Модальное окно оформления заказа */}
          {isCheckoutModalOpen && (
            <div className={styles.modalOverlay} onClick={() => setIsCheckoutModalOpen(false)}>
              <div className={styles.checkoutModal} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.modalTitle}>Оформление заказа</h3>
                
                <div className={styles.orderSummary}>
                  <div className={styles.summaryRow}>
                    <span>Товары:</span>
                    <span>{cartItemsCount} шт.</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span>Способ получения:</span>
                    <span>{deliveryMethod === 'pickup' ? 'Самовывоз' : 'Доставка'}</span>
                  </div>
                  <div className={`${styles.summaryRow} ${styles.total}`}>
                    <span>Итого:</span>
                    <span>{formatPrice(cartTotal)} ₽</span>
                  </div>
                </div>
                
                <form className={styles.orderForm} onSubmit={(e) => e.preventDefault()}>
                  <div className={styles.formGroup}>
                    <label htmlFor="name" className={styles.formLabel}>
                      Имя <span className={styles.required}>*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      className={`${styles.formInput} ${fieldErrors.name ? styles.inputError : ''}`}
                      value={orderForm.name}
                      onChange={(e) => {
                        handleOrderFormChange('name', e.target.value);
                        if (fieldErrors.name) {
                          setFieldErrors((prev) => ({ ...prev, name: false }));
                        }
                      }}
                      placeholder="Введите ваше имя"
                      required
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="phone" className={styles.formLabel}>
                      Телефон <span className={styles.required}>*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      className={`${styles.formInput} ${fieldErrors.phone ? styles.inputError : ''}`}
                      value={orderForm.phone}
                      onChange={(e) => {
                        handleOrderFormChange('phone', e.target.value);
                        if (fieldErrors.phone) {
                          setFieldErrors((prev) => ({ ...prev, phone: false }));
                        }
                      }}
                      placeholder="+7 (___) ___-__-__"
                      required
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="email" className={styles.formLabel}>
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className={styles.formInput}
                      value={orderForm.email}
                      onChange={(e) => handleOrderFormChange('email', e.target.value)}
                      placeholder="example@mail.com"
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="comment" className={styles.formLabel}>
                      Комментарий к заказу
                    </label>
                    <textarea
                      id="comment"
                      className={styles.formTextarea}
                      value={orderForm.comment}
                      onChange={(e) => handleOrderFormChange('comment', e.target.value)}
                      placeholder="Дополнительная информация..."
                      rows={3}
                    />
                  </div>
                </form>
                
                <div className={styles.modalActions}>
                  <button 
                    className={styles.cancelButton}
                    onClick={() => setIsCheckoutModalOpen(false)}
                  >
                    Отмена
                  </button>
                  <button 
                    className={styles.confirmButton}
                    onClick={handleNextToPayment}
                  >
                    Далее
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Модальное окно выбора способа оплаты */}
          {isPaymentMethodModalOpen && (
            <div className={styles.modalOverlay} onClick={() => setIsPaymentMethodModalOpen(false)}>
              <div className={styles.paymentMethodModal} onClick={(e) => e.stopPropagation()}>
                <h3 className={styles.modalTitle}>Выберите способ оплаты</h3>
                
                <div className={styles.paymentOptions}>
                  {/* Вариант 1: Менеджер свяжется */}
                  <label 
                    className={`${styles.paymentOption} ${selectedPaymentMethod === 'manager' ? styles.selected : ''}`}
                    onClick={() => setSelectedPaymentMethod('manager')}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="manager"
                      checked={selectedPaymentMethod === 'manager'}
                      onChange={() => setSelectedPaymentMethod('manager')}
                    />
                    <div className={styles.optionContent}>
                      <div className={styles.optionHeader}>
                        <span className={styles.optionTitle}>Оформление через менеджера</span>
                      </div>
                      <p className={styles.optionDescription}>
                        Наш менеджер свяжется с вами для завершения заказа и уточнения деталей
                      </p>
                    </div>
                  </label>
                  
                  {/* Вариант 2: QR код (недоступен) */}
                  <label 
                    className={`${styles.paymentOption} ${styles.disabled}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="qr"
                      disabled
                    />
                    <div className={styles.optionContent}>
                      <div className={styles.optionHeader}>
                        <span className={styles.optionTitle}>Оплата по QR коду</span>
                      </div>
                      <p className={styles.optionDescription}>
                        <span className={styles.unavailable}>Временно недоступно</span>
                      </p>
                    </div>
                  </label>
                </div>

                {selectedPaymentMethod === 'manager' && (
                  <div className={styles.paymentInfo}>
                    <p className={styles.paymentInfoText}>
                      Нажимая на кнопку "Оформить заказ", Вы соглашаетесь с {agreementLinks[0] && parseTextWithLinks(agreementLinks[0])} и принимаете условия {agreementLinks[1] && parseTextWithLinks(agreementLinks[1])}
                    </p>
                  </div>
                )}
                
                <div className={styles.modalActions}>
                  <button 
                    className={styles.cancelButton}
                    onClick={() => {
                      setIsPaymentMethodModalOpen(false);
                      setIsCheckoutModalOpen(true);
                    }}
                  >
                    Назад
                  </button>
                  <button 
                    className={styles.confirmButton}
                    onClick={handleSubmitOrder}
                    disabled={selectedPaymentMethod !== 'manager'}
                  >
                    Оформить заказ
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredBlocks.length > 0 && (
          <div className={styles.blocksContainer}>
            {filteredBlocks.map((block, index) => renderBlock(block, index))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CartV1;

